import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, Globe, Trash2, Edit2, Search, ChevronUp, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FacebookIcon, XIcon } from '../../../shared/ui/BrandIcons'
import { normalizePlatform, PLATFORM_OPTIONS } from '../../posts/model/platforms'
import type {
  Source,
  SourcesQuery,
  SourceSortField,
  CreateSourceInput,
  UpdateSourceInput,
} from '../model/contracts'
import { SourceForm } from './SourceForm'
import { SourceDetail } from './SourceDetail'
import { Button } from '../../../shared/ui/Button'
import { SourceRowSkeleton } from './SourceSkeleton'

interface SourceListProps {
  sources: Source[]
  total: number
  query: SourcesQuery
  onSearchChange: (search: string) => void
  onPlatformChange: (platform: string) => void
  onIsEnabledChange: (value: string) => void
  onSortChange: (field: SourceSortField) => void
  onPageChange: (page: number) => void
  onCreate: (data: CreateSourceInput) => void
  onUpdate: (data: UpdateSourceInput) => void
  onDelete: (id: string) => void
  isLoading?: boolean
}

const PlatformIcon = ({ platform, size = 16 }: { platform: string; size?: number }) => {
  const p = normalizePlatform(platform)
  if (p === 'facebook') return <FacebookIcon size={size} color="currentColor" />
  if (p === 'x') return <XIcon size={size} color="currentColor" />
  return <Globe size={size} />
}

export function SourceList({
  sources,
  total,
  query,
  onSearchChange,
  onPlatformChange,
  onIsEnabledChange,
  onSortChange,
  onPageChange,
  onCreate,
  onUpdate,
  onDelete,
  isLoading,
}: SourceListProps) {
  const { t } = useTranslation()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [viewingId, setViewingId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024)

  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth >= 1024)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleEdit = (source: Source) => {
    setEditingId(source.id)
    setViewingId(null)
    setShowForm(false)
  }

  const handleView = (source: Source) => {
    setViewingId(source.id)
    setEditingId(null)
    setShowForm(false)
  }

  const handleCancel = () => {
    setEditingId(null)
    setViewingId(null)
    setShowForm(false)
  }

  const handleSubmit = (data: CreateSourceInput | UpdateSourceInput) => {
    if ('id' in data) {
      onUpdate(data)
      setEditingId(null)
    } else {
      onCreate(data as CreateSourceInput)
      setShowForm(false)
    }
  }

  const editingSource = editingId ? sources.find((a) => a.id === editingId) : undefined
  const combinedFilterValue =
    query.platform && query.platform !== 'all'
      ? `platform:${query.platform}`
      : query.isEnabled === null || query.isEnabled === undefined
        ? 'all'
        : `status:${String(query.isEnabled)}`

  const gridTemplate = isLargeScreen
    ? 'minmax(200px, 3fr) 180px 180px 120px'
    : '1fr 120px'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`
        .source-list-container {
          display: flex;
          flex-direction: column;
          border-radius: 16px;
          border: 1px solid var(--border-default);
          overflow: hidden;
          background: var(--surface-card);
        }
        .source-grid-header {
          display: grid;
          grid-template-columns: ${gridTemplate};
          padding: 12px 24px;
          background: var(--surface-1);
          border-bottom: 1px solid var(--border-default);
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-2);
        }
        .source-grid-item {
          display: grid;
          grid-template-columns: ${gridTemplate};
          padding: 20px 24px;
          align-items: center;
          border-bottom: 1px solid var(--border-default);
          background: var(--surface-card);
          transition: background 0.15s ease;
        }
        .source-grid-item:hover {
          background: var(--surface-1);
        }
        .hide-mobile {
          display: ${isLargeScreen ? 'flex' : 'none'};
        }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
            {t('sources.title')}
          </h2>
          {!showForm && !editingId && !viewingId && (
            <Button onClick={() => setShowForm(true)}>{t('sources.addSource')}</Button>
          )}
        </div>

        {/* Search bar — rounded-full with backdrop-blur */}
        <div className="relative">
          <Search
            size={16}
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder={t('sources.searchPlaceholder')}
            value={query.search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-10 w-full rounded-full border border-border bg-muted/30 pl-10 pr-4 text-sm text-foreground outline-none backdrop-blur-md transition-all placeholder:text-muted-foreground/60 focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
          />
        </div>

        {/* Scrollable filter chips */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
          {[
            { value: 'all', label: t('filters.allPlatforms') },
            ...PLATFORM_OPTIONS.map((p) => ({
              value: `platform:${p}`,
              label: t(`platforms.${p}`),
            })),
            { value: 'status:true', label: t('sources.enabled') },
            { value: 'status:false', label: t('sources.disabled') },
          ].map((chip) => (
            <button
              key={chip.value}
              type="button"
              onClick={() => {
                if (chip.value === 'all') {
                  onPlatformChange('all')
                  onIsEnabledChange('all')
                } else if (chip.value.startsWith('platform:')) {
                  onPlatformChange(chip.value.replace('platform:', ''))
                  onIsEnabledChange('all')
                } else if (chip.value.startsWith('status:')) {
                  onPlatformChange('all')
                  onIsEnabledChange(chip.value.replace('status:', ''))
                }
              }}
              className={cn(
                'shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all active:scale-95',
                combinedFilterValue === chip.value
                  ? 'bg-chip-active-surface text-chip-active ring-1 ring-chip-active-ring'
                  : 'bg-secondary/50 text-muted-foreground ring-1 ring-border/50 hover:bg-secondary',
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {(showForm || editingSource) && (
        <div
          style={{
            padding: 24,
            borderRadius: 12,
            border: '1px solid var(--panel-border)',
            background: 'var(--panel)',
          }}
        >
          <SourceForm
            source={editingSource}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        </div>
      )}

      {viewingId && <SourceDetail sourceId={viewingId} onClose={handleCancel} />}

      <div className="source-list-container">
        <div className="source-grid-header">
          {(() => {
            const getSortIcon = (field: SourceSortField) => {
              const sort = query.sort || ''
              const isDesc = sort.startsWith('-')
              const currentField = isDesc
                ? sort.slice(1)
                : sort.startsWith('+')
                  ? sort.slice(1)
                  : sort
              if (currentField !== field) return null
              return isDesc ? <ChevronDown size={14} /> : <ChevronUp size={14} />
            }
            return (
              <>
                <button
                  type="button"
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    color: 'inherit',
                  }}
                  onClick={() => onSortChange('name')}
                >
                  {t('sources.columnSource')} {getSortIcon('name')}
                </button>
                {isLargeScreen && (
                  <>
                    <button
                      type="button"
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        color: 'inherit',
                      }}
                      onClick={() => onSortChange('createdAt')}
                    >
                      {t('sources.columnCreated')} {getSortIcon('createdAt')}
                    </button>
                    <button
                      type="button"
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        color: 'inherit',
                      }}
                      onClick={() => onSortChange('updatedAt')}
                    >
                      {t('sources.columnUpdated')} {getSortIcon('updatedAt')}
                    </button>
                  </>
                )}
              </>
            )
          })()}
          <div style={{ textAlign: 'right' }}>{t('sources.columnActions')}</div>
        </div>

        {isLoading ? (
          [...Array(5)].map((_, i) => (
            <SourceRowSkeleton key={i} isLargeScreen={isLargeScreen} />
          ))
        ) : sources.length === 0 ? (
          <div className="flex flex-col items-center gap-5 px-6 py-20 text-center">
            <div className="empty-icon-pulse flex h-16 w-16 items-center justify-center rounded-2xl bg-nav-active-surface shadow-[0_0_20px_var(--nav-active-glow)]">
              <Globe size={28} className="text-primary" />
            </div>
            <div>
              <div className="text-lg font-semibold text-foreground">
                {t('sources.noSources')}
              </div>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {t('sources.noSourcesDescription', {
                  defaultValue: 'Add your first source to start monitoring.',
                })}
              </p>
            </div>
            <Button onClick={() => setShowForm(true)} className="btn-shimmer">
              {t('sources.addSource')}
            </Button>
          </div>
        ) : (
          sources.map((source) => (
            <div key={source.id} className="source-grid-item">
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}
              >
                <div
                  className="bg-accent-tint"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--accent)',
                    flexShrink: 0,
                  }}
                  title={source.platform}
                >
                  <PlatformIcon platform={source.platform} size={20} />
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      color: 'var(--text)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: 15,
                    }}
                  >
                    {source.displayName || source.name || source.username}
                  </span>
                  {source.username && (
                    <span
                      style={{
                        fontSize: 13,
                        color: 'var(--text-muted)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      @{source.username}
                    </span>
                  )}
                  <span
                    className={cn(
                      source.isEnabled
                        ? 'bg-status-enabled-surface text-status-enabled'
                        : 'bg-status-disabled-surface text-status-disabled',
                    )}
                    style={{
                      alignSelf: 'flex-start',
                      marginTop: 6,
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    {source.isEnabled ? t('sources.enabled') : t('sources.disabled')}
                  </span>
                </div>
              </div>

              {isLargeScreen && (
                <>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {new Date(source.createdAt).toLocaleDateString()}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {new Date(source.updatedAt).toLocaleDateString()}
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                <Button
                  variant="neutral"
                  onClick={() => handleView(source)}
                  style={{ width: 36, height: 36, padding: 0 }}
                >
                  <Eye size={16} />
                </Button>
                <Button
                  variant="neutral"
                  onClick={() => handleEdit(source)}
                  style={{ width: 36, height: 36, padding: 0 }}
                >
                  <Edit2 size={16} />
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    if (confirm(t('sources.deleteConfirm'))) onDelete(source.id)
                  }}
                  style={{ width: 36, height: 36, padding: 0 }}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {total > (query.limit || 20) && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
          <Button
            variant="neutral"
            disabled={query.page === 1}
            onClick={() => onPageChange((query.page || 1) - 1)}
          >
            {t('sources.prev')}
          </Button>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 16px',
              fontSize: 14,
              color: 'var(--text-secondary)',
            }}
          >
            {t('sources.pageOf', {
              current: query.page,
              total: Math.ceil(total / (query.limit || 20)),
            })}
          </div>
          <Button
            variant="neutral"
            disabled={query.page === Math.ceil(total / (query.limit || 20))}
            onClick={() => onPageChange((query.page || 1) + 1)}
          >
            {t('sources.next')}
          </Button>
        </div>
      )}
    </div>
  )
}
